// Diario del dictado: texto transcrito = texto que no se pierde.
//
// PROBLEMA QUE RESUELVE. El buffer de celdas ([[turno-draft-buffer]]) protege lo
// que el médico ESCRIBE, porque siempre tiene una celda de destino. El dictado
// no: entre que Deepgram confirma una frase y esa frase entra a una celda hay un
// tramo donde el texto existe solo en el estado de React. Ahí se perdía:
//
//   · la pestaña se recarga o el PWA se mata en background → se va todo lo
//     dictado que no se hubiera volcado con "Listo";
//   · `commitMicStage` no encuentra dónde escribir y no hay celda de origen
//     (pasa con el dictado remoto si el computador todavía no eligió destino):
//     el texto se descartaba en silencio;
//   · el celular no logra subir un tramo: quedaba marcado "falló" en pantalla y
//     moría con la pestaña.
//
// La regla acá es simple y no admite excepciones: **una frase confirmada por
// Deepgram se anota en este diario ANTES que en ningún otro lado**, y solo se
// borra cuando está guardada de verdad (en una celda, cuyo guardado a su vez
// tiene su propio write-ahead) o cuando el médico decide descartarla.
//
// A diferencia del buffer de celdas, acá NO gana la última intención: cada
// frase es contenido único. Se acumulan, no se pisan.
//
// ⚠️ PHI (Ley 21.719). Esto deja texto clínico en el localStorage del aparato,
// que en un hospital puede ser compartido. Por eso: ventana corta (12 h, la
// mitad que los borradores de celda), se borra apenas se confirma el guardado, y
// `purgarDictado()` se llama al cerrar sesión. Es una superficie de datos nueva
// y está declarada como tal.

const PREFIX = "turno.dictado.v1:";
// Más corto que el buffer de celdas a propósito: esto es texto sin destino
// asignado, lo más sensible de tener dando vueltas en un computador de turno.
const MAX_AGE_MS = 12 * 60 * 60 * 1000;
// Techo defensivo. Un turno entero dictado no llega a esto ni de lejos; existe
// para que un bucle no llene el localStorage y rompa el guardado de celdas, que
// es más importante que el diario.
const MAX_TOTAL_CHARS = 400_000;

const storage = () => {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    // Safari en modo privado puede tirar al tocar localStorage.
    return null;
  }
};

let contador = 0;
const nuevoId = () => {
  contador += 1;
  return `${Date.now().toString(36)}-${contador}-${Math.random().toString(36).slice(2, 8)}`;
};

const entradas = (ls) => {
  const out = [];
  for (let i = 0; i < ls.length; i += 1) {
    const key = ls.key(i);
    if (!key || !key.startsWith(PREFIX)) continue;
    let payload = null;
    try {
      payload = JSON.parse(ls.getItem(key) || "null");
    } catch {
      payload = null;
    }
    if (!payload || typeof payload.texto !== "string" || !payload.texto) {
      out.push({ key, corrupta: true });
      continue;
    }
    out.push({
      key,
      id: key.slice(PREFIX.length),
      texto: payload.texto,
      tab: payload.tab ?? null,
      row: payload.row ?? null,
      rowId: payload.rowId ?? null,
      column: payload.column ?? null,
      origen: payload.origen || "turno",
      origenId: payload.origenId ?? null,
      ts: Number(payload.ts) || 0,
    });
  }
  return out;
};

const borrar = (ls, key) => {
  try {
    ls.removeItem(key);
  } catch {
    /* nada que hacer */
  }
};

/** Tira lo vencido y lo corrupto; si sigue por encima del techo, lo más viejo.
 *  Devuelve las entradas vivas que quedaron, de la más vieja a la más nueva. */
const hacerLugar = (ls) => {
  const ahora = Date.now();
  let total = 0;
  const vivas = [];
  entradas(ls).forEach((e) => {
    if (e.corrupta || !e.ts || ahora - e.ts > MAX_AGE_MS) {
      borrar(ls, e.key);
      return;
    }
    total += e.texto.length;
    vivas.push(e);
  });
  vivas.sort((a, b) => a.ts - b.ts);
  if (total <= MAX_TOTAL_CHARS) return vivas;
  while (vivas.length && total > MAX_TOTAL_CHARS) {
    const e = vivas.shift();
    borrar(ls, e.key);
    total -= e.texto.length;
  }
  return vivas;
};

/**
 * Anota una frase recién transcrita. Devuelve el id con el que después se
 * confirma, o null si no se pudo anotar (sin localStorage, cuota llena).
 *
 * Nunca lanza: si el diario falla, el dictado tiene que seguir funcionando.
 */
export const anotarDictado = ({ texto, tab, row, rowId = null, column, origen, origenId } = {}) => {
  const limpio = String(texto ?? "").trim();
  if (!limpio) return null;
  const ls = storage();
  if (!ls) return null;
  // `origenId` es el id del tramo en el servidor (solo dictado remoto). La
  // entrega es "al menos una vez", así que un mismo tramo puede llegar dos veces
  // si la pestaña murió antes de acusarlo. Sin esta guarda, esa segunda entrega
  // dejaría la misma frase dos veces en el diario y el médico la vería repetida
  // en el rescate.
  if (origenId != null) {
    const yaEsta = entradas(ls).find(
      (e) => !e.corrupta && e.origenId != null && String(e.origenId) === String(origenId),
    );
    if (yaEsta) return yaEsta.id;
  }
  const id = nuevoId();
  const payload = JSON.stringify({
    texto: limpio,
    tab: tab ?? null,
    row: row ?? null,
    // #1449 H-E: identidad (`_id`) del paciente donde se dictó. El número de
    // fila se reusa: sin esto, "Poner en cama N" podía dejar la frase en la
    // ficha del paciente que heredó el número.
    ...(rowId != null ? { rowId } : {}),
    column: column ?? null,
    origen: origen || "turno",
    origenId: origenId ?? null,
    ts: Date.now(),
  });
  try {
    ls.setItem(PREFIX + id, payload);
    return id;
  } catch {
    // Cuota llena. Ojo con la trampa: el techo propio (MAX_TOTAL_CHARS) NO es la
    // cuota real del navegador, que además se comparte con los borradores de
    // celda y con todo lo demás. Mirar solo el techo propio hacía que acá no se
    // liberara nada y la frase perdida terminara siendo **la que el médico
    // acababa de decir**, que es exactamente la que más importa.
    //
    // Se sacrifica de la más vieja a la más nueva hasta que la nueva entre: la
    // vieja ya tuvo todas las oportunidades de guardarse en su celda.
    let vivas = [];
    try {
      vivas = hacerLugar(ls);
    } catch {
      vivas = [];
    }
    // La cota se fija ANTES del loop: usar `vivas.length` como tope mientras se
    // le hace shift la achica en cada vuelta y corta los reintentos a la mitad.
    const sacrificables = vivas.length;
    for (let intento = 0; intento <= sacrificables; intento += 1) {
      try {
        ls.setItem(PREFIX + id, payload);
        return id;
      } catch {
        const masVieja = vivas.shift();
        if (!masVieja) break;
        borrar(ls, masVieja.key);
      }
    }
    // Ni sacrificando todo el diario entra: el localStorage está lleno de otra
    // cosa. Se devuelve null, y el llamador NO debe interpretarlo como
    // "ya está respaldado" — por eso `commitMicStage` no descarta con null.
    return null;
  }
};

/** Ese texto ya está guardado de verdad: sale del diario. */
export const confirmarDictado = (id) => {
  const ls = storage();
  if (!ls || !id) return;
  borrar(ls, PREFIX + id);
};

export const confirmarVarios = (ids) => {
  (ids || []).forEach((id) => confirmarDictado(id));
};

/** Lo mismo que confirmar, pero cuando lo decide el médico y no el sistema. */
export const descartarDictado = confirmarDictado;

/**
 * Frases dictadas que nunca se confirmaron, de la más vieja a la más nueva.
 * Filtra (y limpia) lo vencido en el camino.
 */
export const listarDictado = ({ tab } = {}) => {
  const ls = storage();
  if (!ls) return [];
  const ahora = Date.now();
  const out = [];
  entradas(ls).forEach((e) => {
    if (e.corrupta || !e.ts || ahora - e.ts > MAX_AGE_MS) {
      borrar(ls, e.key);
      return;
    }
    if (tab != null && String(e.tab) !== String(tab)) return;
    out.push({
      id: e.id, texto: e.texto, tab: e.tab, row: e.row, column: e.column,
      origen: e.origen, origenId: e.origenId, ts: e.ts,
      ...(e.rowId != null ? { rowId: e.rowId } : {}),
    });
  });
  out.sort((a, b) => a.ts - b.ts);
  return out;
};

/** Se llama al cerrar sesión: no dejar texto clínico en un computador de turno. */
export const purgarDictado = () => {
  const ls = storage();
  if (!ls) return;
  entradas(ls).forEach((e) => borrar(ls, e.key));
};
